import { useCallback, useEffect, useRef, useState } from 'react';

import { MEMBERSHIP_COUNT_POLL_TIMEOUT_MS } from '../segments.constants';

type UseSegmentMembershipCountPollingParams = {
  membershipCount: number | undefined;
  hasIncompleteFiles: boolean;
  refetchSegment: () => void | Promise<unknown>;
};

export const useSegmentMembershipCountPolling = ({
  membershipCount,
  hasIncompleteFiles,
  refetchSegment,
}: UseSegmentMembershipCountPollingParams) => {
  const [baselineMembershipCount, setBaselineMembershipCount] = useState<number | null>(null);

  const previouslyHadIncompleteFiles = useRef(false);
  const membershipCountAtUploadStartRef = useRef<number | null>(null);

  const onMembershipsChanged = useCallback((baseline: number) => {
    setBaselineMembershipCount(baseline);
  }, []);

  useEffect(() => {
    const wasIncomplete = previouslyHadIncompleteFiles.current;

    if (!wasIncomplete && hasIncompleteFiles) {
      membershipCountAtUploadStartRef.current = membershipCount ?? null;
    } else if (
      hasIncompleteFiles &&
      membershipCount != null &&
      membershipCountAtUploadStartRef.current === null
    ) {
      membershipCountAtUploadStartRef.current = membershipCount;
    }

    previouslyHadIncompleteFiles.current = hasIncompleteFiles;

    if (wasIncomplete && !hasIncompleteFiles) {
      void Promise.resolve(refetchSegment()).catch(() => undefined);

      const baseline = membershipCountAtUploadStartRef.current ?? membershipCount;
      if (baseline != null) {
        onMembershipsChanged(baseline);
      }
    }
  }, [hasIncompleteFiles, membershipCount, refetchSegment, onMembershipsChanged]);

  useEffect(() => {
    if (baselineMembershipCount === null || membershipCount == null) {
      return;
    }

    if (membershipCount !== baselineMembershipCount) {
      setBaselineMembershipCount(null);
    }
  }, [baselineMembershipCount, membershipCount]);

  useEffect(() => {
    if (baselineMembershipCount === null) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setBaselineMembershipCount(null);
    }, MEMBERSHIP_COUNT_POLL_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [baselineMembershipCount]);

  const shouldPollSegment =
    baselineMembershipCount !== null &&
    !hasIncompleteFiles &&
    membershipCount === baselineMembershipCount;

  return {
    shouldPollSegment,
    onMembershipsChanged,
  };
};
